import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import faqService from "./faqService";

const errMsg = (error) =>
  error.response?.data?.message || error.message || "Something went wrong";

const makeThunk = (type, fn) =>
  createAsyncThunk(type, async (arg, thunkAPI) => {
    try {
      return await fn(arg);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  });

export const fetchFaqs = makeThunk("faq/fetchAll", async () => (await faqService.getAll()).data);
export const createFaq = makeThunk("faq/create", async (data) => (await faqService.create(data)).data);
export const updateFaq = makeThunk("faq/update", async (data) => (await faqService.update(data)).data);
export const deleteFaq = makeThunk("faq/delete", async (id) => {
  await faqService.remove(id);
  return id;
});

const bySort = (a, b) => a.sort_order - b.sort_order || a.id - b.id;

const faqSlice = createSlice({
  name: "faq",
  initialState: {
    list: [],
    isLoading: false,
    isSaving: false,
    isError: false,
    message: "",
    errors: {},
  },
  reducers: {
    clearFaqError: (s) => { s.isError = false; s.message = ""; s.errors = {}; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaqs.pending, (s) => { s.isLoading = true; })
      .addCase(fetchFaqs.fulfilled, (s, a) => { s.isLoading = false; s.list = a.payload; })
      .addCase(fetchFaqs.rejected, (s, a) => {
        s.isLoading = false; s.isError = true;
        s.message = a.payload?.message || "Failed to load FAQs";
      })

      .addCase(createFaq.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(createFaq.fulfilled, (s, a) => { s.isSaving = false; s.list.push(a.payload); s.list.sort(bySort); })
      .addCase(createFaq.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Create failed";
        s.errors = a.payload?.errors || {};
      })

      .addCase(updateFaq.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(updateFaq.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.map((f) => (f.id === a.payload.id ? a.payload : f)).sort(bySort);
      })
      .addCase(updateFaq.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Update failed";
        s.errors = a.payload?.errors || {};
      })

      .addCase(deleteFaq.pending, (s) => { s.isSaving = true; s.isError = false; })
      .addCase(deleteFaq.fulfilled, (s, a) => { s.isSaving = false; s.list = s.list.filter((f) => f.id !== a.payload); })
      .addCase(deleteFaq.rejected, (s, a) => {
        s.isSaving = false; s.isError = true; s.message = a.payload?.message || "Delete failed";
      });
  },
});

export const { clearFaqError } = faqSlice.actions;
export default faqSlice.reducer;